/**
 * What a game said about a session, for the session page.
 *
 *   session.header             the game's Header as the session started (portal
 *                              routes/helpers/sessions.header.js; shape headerVersion 1,
 *                              egdl_Core GAMES-UPGRADE-PLAN.md): version, platform, how it
 *                              was started, who played, tracker, settings in effect, mode,
 *                              adaptive difficulty, multiplayer.
 *   session.results.customDetails  the Footer's scoreDetails: the game's own metrics.
 *
 * Pure functions: rows of { label, value } for KVGrid, nothing rendered here.
 *
 * The Header and the Footer are whatever the game sent, so nothing here trusts their shape:
 * every row value is a string or null (show), names are looked up only among a table's own
 * keys ("__proto__", "toString" and "constructor" are text, not Object.prototype), and nothing
 * is put through a template literal or String() unless it is already a string or a number
 * (an object whose "toString" is not a function would throw there). Security audit 2026-10-18, M2.
 */

const STARTED_FROM = { launcher: 'EnAble Launcher', browser: 'Web browser', direct: 'Opened directly' };
const TRACKERS = { mediapipe: 'Webcam (MediaPipe)', phone: 'Phone', kinect: 'Kinect', none: 'No tracker' };

/** table[key] when key is one of the table's own names, else null. */
function lookup(table, key) {
    return typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null;
}

/** Nested objects deeper than this show as "…" (a game could send any depth). */
const MAX_DEPTH = 4;

/** "gameLength" / "games_won" / "Hit Rate" → "Game length" / "Games won" / "Hit rate" */
export function humanize(key) {
    if (typeof key !== 'string' && typeof key !== 'number') return '';
    const words = String(key)
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim()
        .toLowerCase();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

/** A value as one line of text, or null when there is nothing to show. Always a string or null. */
export function show(value, depth = 0) {
    if (value == null || value === '') return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return String(value);
        return Number.isInteger(value) ? value.toLocaleString() : (+value.toFixed(2)).toLocaleString();
    }
    if (typeof value === 'bigint') return value.toLocaleString();
    if (typeof value !== 'object') return null; // functions, symbols: nothing a game's JSON can hold
    if (depth >= MAX_DEPTH) return '…';
    if (Array.isArray(value)) {
        const parts = value.map((v) => show(v, depth + 1)).filter(Boolean);
        return parts.length ? parts.join(', ') : null;
    }
    const parts = Object.keys(value).map((k) => {
        const s = show(value[k], depth + 1);
        return s == null ? null : `${humanize(k)}: ${s}`;
    }).filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
}

/** Who played, in words: the patient's name, "Guest · label", or null for the player themself. */
function playedBy(header, nameOf) {
    const who = header.playingFor;
    if (who === 'guest') {
        const label = show(header.guestLabel);
        return label ? `Guest · ${label}` : 'Guest';
    }
    if (!who || who === 'self') return null;
    if (typeof who !== 'string') return show(who); // a patient id is a string; anything else, as text
    const name = nameOf?.(who);
    return typeof name === 'string' && name ? name : who;
}

/** Rows describing how the session started; [] when the session has no header. */
export function headerRows(header, nameOf) {
    if (!header || typeof header !== 'object' || Array.isArray(header)) return [];
    const tracker = header.tracker && typeof header.tracker === 'object' ? header.tracker : {};
    const source = lookup(TRACKERS, tracker.source)
        || (typeof tracker.source === 'string' ? humanize(tracker.source) : show(tracker.source));
    const trackerText = source ? `${source}${tracker.hands ? ', hands too' : ''}` : null;
    const dda = header.dda && typeof header.dda === 'object' ? header.dda : {};
    const startLevel = show(dda.startLevel);
    const ddaText = dda.enabled == null ? null
        : dda.enabled ? `On${startLevel != null ? `, starting at level ${startLevel}` : ''}` : 'Off';
    const mp = header.multiplayer;
    const rows = [
        { label: 'Game version', value: show(header.version) },
        { label: 'Platform', value: show(header.platform) },
        { label: 'Started from', value: lookup(STARTED_FROM, header.startedFrom) || show(header.startedFrom) },
        { label: 'Played by', value: playedBy(header, nameOf) },
        { label: 'Run by a clinician', value: header.clinicianRun == null ? null : show(header.clinicianRun) },
        { label: 'Tracker', value: trackerText },
        { label: 'Mode', value: show(header.mode) },
        { label: 'Adaptive difficulty', value: ddaText },
        { label: 'Multiplayer', value: mp ? show(mp) : null },
        { label: 'EGDL', value: show(header.egdl) },
    ];
    return rows.filter((r) => r.value != null);
}

/** The settings the game ran with, one row per parameter, and where they came from. */
export function settingsRows(header) {
    const values = header?.settings?.values;
    if (!values || typeof values !== 'object') return [];
    return Object.entries(values)
        .map(([name, value]) => ({ label: name, value: show(value) }))
        .filter((r) => r.value != null);
}

export function settingsSource(header) {
    const source = header?.settings?.source;
    if (source === 'portal') return 'From the portal’s game settings';
    if (source === 'default') return 'The game’s own defaults (no portal settings)';
    // played offline: the portal's settings as this computer last fetched them (egdl_Core GAMES-OFFLINE-SPEC.md §5)
    if (source === 'cached') {
        const fetchedAt = header.settings.fetchedAt;
        const at = typeof fetchedAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(fetchedAt) ? Date.parse(fetchedAt) : NaN;
        return Number.isFinite(at)
            ? `Portal settings fetched ${new Date(at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`
            : 'Portal settings fetched before';
    }
    return null;
}

/** The game's own end-of-session metrics (the Footer's scoreDetails). */
export function detailRows(customDetails) {
    if (!customDetails || typeof customDetails !== 'object' || Array.isArray(customDetails)) return [];
    return Object.entries(customDetails)
        .filter(([key]) => !key.startsWith('$'))
        .map(([key, value]) => ({ label: humanize(key), value: show(value) }))
        .filter((r) => r.value != null);
}
